// If you want to configure one of these caches to be different than what is set here
// then do something like this example in your root project:
// angular.module('module-name').run(['AprimaCache', function(AprimaCache) {
//     'use strict';
//     AprimaCache.Lists.setMaxAge(10 * 60 * 1000);
// }]);
// Or to add another cache or even change a common one from your root project:
// angular.module('module-name').config(['AprimaCacheProvider', function(AprimaCacheProvider) {
//     'use strict';
//     AprimaCacheProvider.addCache({
//         Default: {
//             maxAge: 5 * 60 * 1000,
//             deleteOnExpire: 'aggressive'
//         },
//         NewCache: {
//             maxAge: 10 * 60 * 1000,
//             deleteOnExpire: 'passive'
//         },
//         NewCache2: {
//             maxAge: 10 * 60 * 1000,
//             deleteOnExpire: 'passive'
//         }
//     });
// }]);
// Here is the API for CacheFactory: https://github.com/jmdobry/angular-cache#api-reference

// One of the reasons this is necessary is because sometimes it is useful to have more control over the caching,
// and because iOS doesn't handle Cache-Control headers correctly

(function(angular) {
    'use strict';

    angular.module('aprima-core').provider('AprimaCache', [
        '_',
        AprimaCacheProvider,
    ]);

    function AprimaCacheProvider(
        _
    ) {
        var disableAllCaches = false;

        // IMPORTANT:
        // Using localStorage or sessionStorage for storageMode doesn't work right with promises,
        // because of a bug where the item never gets removed from storage
        // even after it's supposed to be expired (https://github.com/jmdobry/angular-cache/issues/224)

        // These caches are global, so if you change one, it will be changed for everywhere that uses it
        // These below are the common ones
        var cacheSettings = {
            // cache to be used for lists
            Lists: {
                maxAge: 1 * 60 * 60 * 1000, // 60 minutes (1 hour)
                deleteOnExpire: 'passive',
                capacity: 200,
                description: 'All Lists',
            },
            // default http cache
            Default: {
                maxAge: 2 * 60 * 60 * 1000, // 2 hours
                deleteOnExpire: 'passive',
                capacity: 200,
                description: 'General Data Cache',
            },
            Permissions: {
                maxAge: 30 * 60 * 1000, // 30 minutes
                deleteOnExpire: 'passive',
                capacity: 100,
                description: 'Security Permissions',
            },

            // I don't believe any IMO data will change more than once per day (24 hours)
            ImoTerms: {
                maxAge: 24 * 60 * 60 * 1000, // 24 hours
                deleteOnExpire: 'passive',
                storageMode: 'localStorage',
                capacity: 200,
                description: 'Diagnosis Terms',
            },
            ImoDetails: {
                maxAge: 24 * 60 * 60 * 1000, // 24 hours
                deleteOnExpire: 'passive',
                storageMode: 'localStorage',
                capacity: 100,
                description: 'Diagnosis Details',
            }
        };

        // cacheSetting should be like { cacheName: { maxAge: x, .....}}
        this.addCache = addCache;

        // can disable all of these caches completely in config block at the start of the app if needed
        this.disableAll = disableAll;

        this.$get = [
            '_',
            '$log',
            'CacheFactory',
            'ObjUtil',
            AprimaCacheService,
        ];

        function addCache(cacheSetting) {
            _.assign(cacheSettings, cacheSetting);
        }

        function disableAll() {
            disableAllCaches = true;
        }

        function AprimaCacheService(
            _,
            $log,
            CacheFactory,
            ObjUtil
        ) {
            // if the CacheFactory api changes, then the CacheFactoryDecorator needs to be updated as well

            var caches = _.mapValues(cacheSettings, function(settings, cacheName) {
                var newCache = createNew(cacheName, settings);
                return newCache;
            });

            ObjUtil.defineStaticProps(caches, {
                createNew: createNew,
                // these 2 override methods are for when you want to get a cache that won't be
                // automatically disabled based on AprimaCacheProvider.disableAll();
                createNewOverride: createNewClean,
                getOrCreate: getOrCreate,
                getOrCreateOverride: getOrCreateClean,
                get: get,
                info: info,
                keySet: keySet,
                keys: keys,
                destroy: destroy,
                destroyAll: destroyAll,
                clearAll: clearAll,
                enableAll: enableAll,
                disableAll: disableAll,
                touchAll: touchAll,
                removeExpiredFromAll: removeExpiredFromAll,
            });

            return caches;


            function createNew() {
                var cache = createNewClean.apply(null, arguments);
                if(disableAllCaches) {
                    cache.disable();
                }
                return cache;
            }

            function createNewClean(cacheName, options) {
                CacheFactory.destroy(cacheName);
                var cache = CacheFactory.createCache.apply(CacheFactory, arguments);

                // This code below used to be in a decorator of CacheFactory,
                // but because CacheFactory is defined as a singleton outside of angular,
                // the singleton is not re-created each time the modules are reloaded.
                // Because of this, making changes to methods on the singleton was causing issues in the unit tests,
                // since those changes were persisting between tests.
                // That is why it was just simply moved to this wrapper service.
                var description = options && options.description || cacheName;

                var infoFn = cache.info;
                cache.info = function() {
                    var infoFnResult = infoFn.apply(cache, arguments);
                    if(infoFnResult) {
                        infoFnResult.description = description;
                    }
                    return infoFnResult;
                };

                cache.getClone = function() {
                    var getFnResult = cache.get.apply(cache, arguments);
                    var clone = _.cloneDeep(getFnResult);
                    return clone;
                };

                addHttpModes(cache, cacheName);

                return cache;
            }

            function getOrCreate() {
                var cache = getOrCreateClean.apply(null, arguments);
                if(disableAllCaches) {
                    cache.disable();
                }
                return cache;
            }

            function getOrCreateClean(cacheName) {
                var cache = CacheFactory.get(cacheName) || createNewClean(cacheName);
                return cache;
            }

            function get() {
                return CacheFactory.get.apply(CacheFactory, arguments);
            }

            function info() {
                return CacheFactory.info.apply(CacheFactory, arguments);
            }

            function keySet() {
                return CacheFactory.keySet.apply(CacheFactory, arguments);
            }

            function keys() {
                return CacheFactory.keys.apply(CacheFactory, arguments);
            }

            function destroy() {
                return CacheFactory.destroy.apply(CacheFactory, arguments);
            }

            function destroyAll() {
                return CacheFactory.destroyAll.apply(CacheFactory, arguments);
            }

            function clearAll() {
                return CacheFactory.clearAll.apply(CacheFactory, arguments);
            }

            function enableAll() {
                return CacheFactory.enableAll.apply(CacheFactory, arguments);
            }

            function disableAll() {
                return CacheFactory.disableAll.apply(CacheFactory, arguments);
            }

            function touchAll() {
                return CacheFactory.touchAll.apply(CacheFactory, arguments);
            }

            function removeExpiredFromAll() {
                return CacheFactory.removeExpiredFromAll.apply(CacheFactory, arguments);
            }


            // adds httpMode and httpIgnoreMode properties to the cache
            // that will add more logging when passing in to restangular and/or $http
            function addHttpModes(cache, cacheName) {
                if (!cache) {
                    return;
                }

                var httpSubset = {
                    get: getWithHttpLogging,
                    put: wrap(cache, 'put'),
                    remove: wrap(cache, 'remove'),
                    removeAll: wrap(cache, 'removeAll'),
                    info: wrap(cache, 'info'),
                    destroy: wrap(cache, 'destroy'),
                };

                var httpIgnoreSubset = _.defaults({
                    get: getIgnoreCacheWithHttpLogging,
                }, httpSubset);


                // pass these into $http or restangular, instead of just the normal cache object
                ObjUtil.defineReadonlyProps(cache, {
                    httpMode: httpSubset,
                    httpIgnoreMode: httpIgnoreSubset,
                });

                return cache;

                // adds logging for when it is using a cached value
                function getWithHttpLogging(url) {
                    var result = cache.get.apply(cache, arguments);
                    if(angular.isDefined(result)) {
                        var msg = _.isArray(result) ? '($http format: [statusCode, data, headers, statusText])' : '';

                        $log.debug(httpLogPrefix(cacheName), '-- Retrieved "' + url + '" from cache. Result' + msg + ':', result);
                    }

                    return result;
                }

                // have $http ignore the cached value by just returning undefined instead
                // and log when a value is ignored
                function getIgnoreCacheWithHttpLogging(url) {
                    $log.debug(httpLogPrefix(cacheName), '-- Ignored cache for "' + url + '".');
                    return undefined;
                }
            }
        }
    }

    function httpLogPrefix(cacheId) {
        return 'AprimaCache-' + cacheId + '-Http';
    }

    function wrap(obj, methodName) {
        return function() {
            return obj[methodName].apply(obj, arguments);
        };
    }

})(window.angular);
